import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useRuntimeFlags } from "@/contexts/FeatureFlagContext";
import { FEATURE_FLAGS, uiUpliftFromEnv } from "@/lib/feature-flags";
import { getUiVariant, type UiVariant } from "@/lib/ui-uplift";

/**
 * The chassis variant for the current viewer ("classic" | "next"). Re-resolves when
 * runtime flags load or the URL's `?ui=` changes. See src/lib/ui-uplift.ts.
 */
export function useUiUplift(): UiVariant {
  const { search } = useLocation();
  // Subscribes to FeatureFlagProvider, which re-renders this hook when it re-hydrates
  // the static flag store, so the getter below reads the fresh value.
  const { isCurrentForOrganization } = useRuntimeFlags();
  // Right after an org switch the runtime store still holds the previous org's rows; don't
  // show that org's chassis in this one. Env (preview builds) still applies.
  const flag = isCurrentForOrganization ? FEATURE_FLAGS.uiUplift : uiUpliftFromEnv();
  return useMemo(() => getUiVariant(search, flag), [search, flag]);
}

/**
 * Whether useUiUplift() reflects the current organization's flag yet. Until it does, a cold load
 * reads the env fallback, so a page that fetches differently per variant waits for this.
 */
export function useUiUpliftSettled(): boolean {
  return useRuntimeFlags().isSettled;
}
